import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import {
  TableContainer,
  StyledTable,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TableCell,
  StatusCell,
  EmptyState,
  CellWithTooltipStyled,
  HeaderCellWithFilter,
} from "../styles/Layoutstyle";
import { SkeletonRows } from "../skeleton/skeleton";
import DateFilter from "./DateFilter";
import EOSFilter from "./EOSFilter";
import StatusFilterLogic from "./StatusFilter";
import AccuzipListFilter from "./AccuzipListFilter";
import ColumnToggle from "./ColumnToggle";
import { FiFilter } from "react-icons/fi";
import { FilterButton } from "../styles/FilterStyles";

// ---------- Tooltip Logic ----------
const useTooltipPosition = (cellRef, tooltipRef, isTruncated) => {
  const [tooltipStyle, setTooltipStyle] = useState({});
  const handleMouseEnter = () => {
    if (cellRef.current && isTruncated) {
      requestAnimationFrame(() => {
        const cellRect = cellRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const minMargin = 8;
        let left = cellRect.left + cellRect.width / 2 - tooltipRect.width / 2;
        if (left < minMargin) left = minMargin;
        if (left + tooltipRect.width > viewportWidth - minMargin)
          left = viewportWidth - tooltipRect.width - minMargin;
        const top =
          cellRect.top - tooltipRect.height - 8 < minMargin
            ? cellRect.bottom + 8
            : cellRect.top - tooltipRect.height - 8;
        setTooltipStyle({ top, left, transform: "none" });
      });
    }
  };
  return { tooltipStyle, handleMouseEnter };
};

// ---------- Tooltip Cell Components ----------
const CellWithTooltip = ({ value, label, isDarkMode }) => {
  const cellRef = useRef(null);
  const tooltipRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const stringValue = String(value || "N/A");
  const { tooltipStyle, handleMouseEnter } = useTooltipPosition(
    cellRef,
    tooltipRef,
    isTruncated,
  );
  useEffect(() => {
    const el = cellRef.current?.querySelector(".cell-content");
    if (el) setIsTruncated(el.scrollWidth > el.clientWidth);
  }, [value]);
  if (stringValue === "N/A")
    return (
      <TableCell data-label={label} isDarkMode={isDarkMode}>
        {value}
      </TableCell>
    );
  return (
    <CellWithTooltipStyled
      ref={cellRef}
      hasTooltip={isTruncated}
      data-label={label}
      onMouseEnter={handleMouseEnter}
      isDarkMode={isDarkMode}
    >
      <span className="cell-content">{value}</span>
      {isTruncated && (
        <div ref={tooltipRef} className="tooltip" style={tooltipStyle}>
          {stringValue}
        </div>
      )}
    </CellWithTooltipStyled>
  );
};

// ---------- Main Orders Table ----------
const OrdersTable = ({
  orders,
  loading,
  isSearching,
  searchResults,
  searchTerm,
  formatDate,
  onApplyFilter,
  onStreetViewFilter,
  columnFilters,
  onStatusFilterChange,
  statusFilter,
  availableStatuses,
  isDarkMode,
}) => {
  const containerRef = useRef(null);
  const resizingRef = useRef({ col: null, startX: 0, startWidth: 0 });

  const getHeaderWidth = (colIndex) => {
    try {
      const th = containerRef.current?.querySelector(
        `thead th:nth-child(${colIndex + 1})`,
      );
      if (th) return th.getBoundingClientRect().width;
    } catch (e) {
      // ignore
    }
    return 100;
  };

  const handleMouseMove = (e) => {
    if (resizingRef.current.col === null) return;
    const { col, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    const newWidth = Math.max(40, startWidth + delta);
    if (containerRef.current) {
      containerRef.current.style.setProperty(
        `--orders-col-${col + 1}-width`,
        `${newWidth}px`,
      );
    }
  };
  console.log(orders, "orders");

  const stopResize = () => {
    resizingRef.current = { col: null, startX: 0, startWidth: 0 };
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", stopResize);
  };

  const handleMouseDown = (e, colIndex) => {
    e.preventDefault();
    const startWidth = getHeaderWidth(colIndex);
    resizingRef.current = { col: colIndex, startX: e.clientX, startWidth };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", stopResize);
  };

  useEffect(() => {
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", stopResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dataToRender = isSearching ? searchResults : orders;
  const isEmpty = dataToRender.length === 0;

  const tableHeaders = [
    { label: "Order ID", key: "orderId", type: "text" },
    { label: "Order Date", key: "orderDate", type: "date" },
    { label: "Status", key: "status", type: "text" },
    { label: "EOS Status", key: "eosStatus", type: "text" },
    { label: "Street View", key: "streetViewRequired", type: "boolean" },
    { label: "Accuzip List", key: "accuzipNumber", type: "text" },
    { label: "Accuzip Start Time", key: "accuzip_startTime", type: "date" },
    { label: "Accuzip End Time", key: "accuzip_endTime", type: "date" },
    { label: "Total", key: "orderTotal", type: "number" },
    { label: "Street View Done", key: "streetViewCompleted", type: "boolean" },
    { label: "SV Start Time", key: "streetStartTime", type: "date" },
    { label: "EOS Start Time", key: "eosStartTime", type: "date" },
    {
      label: "SV Completion Date",
      key: "streetViewCompletionDate",
      type: "date",
    },
    {
      label: "SV Completion Date",
      key: "streetViewCompletionDateTime",
      type: "date",
    },
  ];

  // Initialize visible columns state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const initial = {};
    tableHeaders.forEach((header) => {
      initial[header.key] = true;
    });
    return initial;
  });

  const renderOrderRow = (order) => (
    <TableRow
      key={order.id || order.orderId}
      status={order.status}
      isDarkMode={isDarkMode}
    >
      {visibleColumns.orderId && (
        <CellWithTooltip
          value={order.orderId}
          label="Order ID"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.orderDate && (
        <TableCell data-label="Order Date" isDarkMode={isDarkMode}>
          {formatDate(order.created_date)?.split(", ").slice(0, 2).join(", ") ||
            "—"}
        </TableCell>
      )}
      {visibleColumns.status && (
        <StatusCell
          status={order.status}
          data-label="Status"
          isDarkMode={isDarkMode}
        >
          {order.status
            ?.replace(/[_-]/g, " ")
            .replace(/\b\w/g, (m) => m.toUpperCase())}
        </StatusCell>
      )}
      {visibleColumns.eosStatus && (
        <CellWithTooltip
          value={order.eosStatus || "N/A"}
          label="EOS Status"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.streetViewRequired && (
        <CellWithTooltip
          value={order.streetViewRequired ? "✅ Yes" : "❌ No"}
          label="Street View"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.accuzipNumber && (
        <CellWithTooltip
          value={order.accuzipNumber}
          label="Accuzip List"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.accuzip_startTime && (
        <CellWithTooltip
          value={
            formatDate(order.accuzip_startTime)
              ?.split(", ")
              .slice(0, 3)
              .join(", ") || "—"
          }
          label="Accuzip Start Time"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.accuzip_endTime && (
        <CellWithTooltip
          value={
            formatDate(order.accuzip_endTime)
              ?.split(", ")
              .slice(0, 3)
              .join(", ") || "—"
          }
          label="Accuzip End Time"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.orderTotal && (
        <CellWithTooltip
          value={order.orderTotal || "N/A"}
          label="Total"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.streetViewCompleted && (
        <CellWithTooltip
          value={
            !order.streetViewRequired
              ? "N/A"
              : order.streetViewCompleted
                ? "✅ Yes"
                : "❌ No"
          }
          label="Street View Done"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.streetStartTime && (
        <CellWithTooltip
          value={
            !order.streetViewRequired
              ? "N/A"
              : formatDate(order.streetStartTime)
          }
          label="SV Start"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.eosStartTime && (
        <CellWithTooltip
          value={!order.eosStartTime ? "N/A" : formatDate(order.eosStartTime)}
          label="EOS Start"
          isDarkMode={isDarkMode}
        />
      )}
      {visibleColumns.streetViewCompletionDate && (
        <CellWithTooltip
          value={
            !order.streetViewRequired
              ? "N/A"
              : formatDate(
                  Number(order.orderId) > 800287
                    ? order.streetViewCompletionDateTime
                    : order.streetViewCompletionDate,
                )
          }
          label="SV Completion Date"
          isDarkMode={isDarkMode}
        />
      )}
    </TableRow>
  );

  return (
    <>
      {/* Column Toggle Button - Add this above your table */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "1rem",
          position: "relative",
          zIndex: 10,
        }}
      >
        <ColumnToggle
          tableHeaders={tableHeaders}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={setVisibleColumns}
          isDarkMode={isDarkMode}
          position="right"
        />
      </div>

      <TableContainer ref={containerRef} isDarkMode={isDarkMode}>
        <StyledTable>
          <TableHeader isDarkMode={isDarkMode}>
            <tr>
              {tableHeaders.map((header, idx) => {
                if (!visibleColumns[header.key]) return null;

                const showDateFilter = [
                  "orderDate",
                  "accuzip_startTime",
                  "accuzip_endTime",
                  "streetViewCompletionDate",
                ].includes(header.key);
                const showStreetViewFilter = [
                  "streetViewRequired",
                  "streetViewCompleted",
                ]?.includes(header.key);
                const showEOSFilter = ["eosStatus"]?.includes(header.key);
                const showStatusFilter = ["status"]?.includes(header.key);
                const showAccuzipFilter = ["accuzipNumber"]?.includes(
                  header.key,
                );

                return (
                  <HeaderCellWithFilter
                    key={header.key}
                    isDarkMode={isDarkMode}
                  >
                    <div className="header-content">
                      <span className="header-text">{header.label}</span>
                      {showDateFilter && (
                        <div className="filter-wrapper">
                          <DateFilter
                            onFilterChange={(field, filter) =>
                              onApplyFilter(header.key, filter)
                            }
                            fieldName={header.key}
                            currentFilter={columnFilters?.[header.key]}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )}
                      {showStreetViewFilter && (
                        <div>
                          <FilterButton onClick={onStreetViewFilter}>
                            <FiFilter />
                          </FilterButton>
                        </div>
                      )}
                      {showEOSFilter && (
                        <div className="filter-wrapper">
                          <EOSFilter
                            onFilterChange={(field, filter) =>
                              onApplyFilter(header.key, filter)
                            }
                            fieldName={header.key}
                            currentFilter={columnFilters?.[header.key]}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )}
                      {showStatusFilter && (
                        <div className="filter-wrapper">
                          <StatusFilterLogic
                            onStatusFilterChange={onStatusFilterChange}
                            availableStatuses={availableStatuses}
                            currentStatus={statusFilter}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )}
                      {showAccuzipFilter && (
                        <div className="filter-wrapper">
                          <AccuzipListFilter
                            onFilterChange={(field, filter) =>
                              onApplyFilter(header.key, filter)
                            }
                            fieldName={header.key}
                            currentFilter={columnFilters?.[header.key]}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                      )}
                    </div>
                    <div
                      className="resizer"
                      title="Drag to resize column"
                      role="separator"
                      onMouseDown={(e) => handleMouseDown(e, idx)}
                      aria-hidden="true"
                    />
                  </HeaderCellWithFilter>
                );
              })}
            </tr>
          </TableHeader>

          <tbody>
            {loading ? (
              <SkeletonRows count={5} isDarkMode={isDarkMode} />
            ) : isEmpty ? (
              <TableRow>
                <EmptyState colSpan={tableHeaders.length}>
                  {isSearching
                    ? `No orders found matching "${searchTerm}"`
                    : "No orders found"}
                </EmptyState>
              </TableRow>
            ) : (
              dataToRender.map(renderOrderRow)
            )}
          </tbody>
        </StyledTable>
      </TableContainer>
    </>
  );
};

export default OrdersTable;
